import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Alert,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { dealFunderListFetchData } from '../../actions/regionActions';
import {
  batchProcessListFetchData,
  runBatchProcessData,
  rollbackBatchProcessData,
} from '../../actions/batchProcessActions';
import { v4 as uuidv4 } from 'uuid';
import NoRowExistsRow from '../../components/common/noRowExistsRow';
import { BeatLoader } from 'react-spinners';

const BatchProcessPage = () => {
  const dispatch = useDispatch();
  const intervalRef = useRef();

  const dealFunderList = useSelector((state) => state.dealFunderList) ?? [];
  const batchProcessList = useSelector(
    (state) => state.batchProcessListSuccess,
  );
  const errorMessage = useSelector((state) =>
    state.runBatchProcessHasErrored
      ? state.runBatchProcessHasErrored.errorMessage
      : '',
  );
  const isLoading = useSelector(
    (state) => state.batchProcessListIsRunning ?? true,
  );

  const [form, setForm] = useState({
    processCode: '',
    startDate: moment().add(1, 'days'),
    endDate: moment().add(30, 'days'),
    dealFunder: '',
    preview: false,
  });
  const [isFormValid, setIsFormValid] = useState(true);
  const [isFunderEnabled, setIsFunderEnabled] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(dealFunderListFetchData());
    dispatch(batchProcessListFetchData());
    intervalRef.current = setInterval(() => {
      dispatch(batchProcessListFetchData());
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [dispatch]);

  useEffect(() => {
    // Reset form if new batch process is added
    setForm((prev) => ({
      ...prev,
      processCode: '',
      startDate: moment().add(-30, 'days'),
      endDate: moment().add(-1, 'days'),
      dealFunder: '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchProcessList && batchProcessList.length]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    enableDisableControls(name, value);
  };

  const handleStartDateChange = (date) => {
    setForm((prev) => ({ ...prev, startDate: date }));
    validateForm({ ...form, startDate: date });
  };

  const handleEndDateChange = (date) => {
    setForm((prev) => ({ ...prev, endDate: date }));
    validateForm({ ...form, endDate: date });
  };

  const enableDisableControls = (fieldName, fieldValue) => {
    if (
      fieldName === 'processCode' &&
      (fieldValue === 'FP' || fieldValue === 'PA')
    ) {
      setIsFunderEnabled(true);
    } else if (
      fieldName === 'processCode' &&
      fieldValue !== 'FP' &&
      fieldValue !== 'PA'
    ) {
      setIsFunderEnabled(false);
      setForm((prev) => ({ ...prev, dealFunder: '' }));
    }
  };

  const validateForm = (model = form) => {
    const newErrors = {};
    const isSDateEmpty = !model.startDate;
    const isEDateEmpty = !model.endDate;
    if (isSDateEmpty || isEDateEmpty) {
      newErrors.startDate = true;
      newErrors.endDate = true;
      setIsFormValid(false);
      setErrors(newErrors);
      return false;
    }
    const sDate = moment(model.startDate).format('DD-MMM-YYYY');
    const eDate = moment(model.endDate).format('DD-MMM-YYYY');
    const isSameOrBefore = moment(new Date(eDate)).isSameOrBefore(
      new Date(sDate),
    );
    if (isSameOrBefore) {
      newErrors.startDate = true;
      newErrors.endDate = true;
    } else {
      newErrors.startDate = false;
      newErrors.endDate = false;
    }
    const valid = !newErrors.startDate && !newErrors.endDate;
    setIsFormValid(valid);
    setErrors(newErrors);
    return valid;
  };

  const formSubmit = (preview) => {
    if (!validateForm()) return;
    const batchProcess = {
      id: uuidv4(),
      processCode: form.processCode,
      startDate: moment(new Date(form.startDate)).format('DD-MMM-YYYY'),
      endDate: moment(new Date(form.endDate)).format('DD-MMM-YYYY'),
      dealFunder: form.dealFunder,
      isPreview: preview,
    };
    dispatch(runBatchProcessData(batchProcess));
  };

  const rollbackBatch = (id, event) => {
    event.target.disabled = true;
    dispatch(rollbackBatchProcessData(id));
  };

  const getStatusString = (status) => {
    switch (status) {
      case 'N':
        return 'New';
      case 'R':
        return 'Running';
      case 'C':
        return 'Completed';
      case 'E':
        return 'Errored';
      default:
        return 'Undefined';
    }
  };

  const getProcessString = (status) => {
    switch (status) {
      case 'SP':
      case 'SPP':
        return 'Store Process';
      case 'FP':
      case 'FPP':
        return 'Funder Process';
      case 'PA':
      case 'PAP':
        return 'Purchase Rebate';
      case 'BCC':
        return 'Bonus Cross Charge';
      default:
        return 'Undefined';
    }
  };

  const renderBatchRow = (rule) => {
    const fileRows =
      rule.files && rule.files.length > 0
        ? rule.files.map((file) => (
            <div key={file.fileName}>
              <a href={`${file.uri}`}>{file.fileName}</a>
            </div>
          ))
        : '';

    const actionRow =
      rule.processCode === 'PAP' ||
      rule.processCode === 'FPP' ||
      rule.processCode === 'SPP' ? (
        'Preview'
      ) : !rule.isRolledBack ? (
        <Button
          className='ml-sm-2'
          name='btnProcess'
          id='btnProcess'
          onClick={(e) => rollbackBatch(rule.id, e)}
          disabled={false}
        >
          Rollback
        </Button>
      ) : (
        'Rollback Successful'
      );

    return (
      <Row className='promotion-row' key={rule.id}>
        <Col md='1' className='white-border'>
          <div> {getProcessString(rule.processCode)} </div>
        </Col>
        <Col md='2' className='white-border'>
          {moment(new Date(rule.startDate)).format('DD MMM YYYY')} -
          {moment(new Date(rule.endDate)).format('DD MMM YYYY')}
        </Col>
        <Col md='1' className='white-border'>
          <div> {rule.dealFunder}</div>
        </Col>
        <Col md='1' className='white-border'>
          {getStatusString(rule.status)}
        </Col>
        <Col md='2' className='white-border'>
          {fileRows}
        </Col>
        <Col md='2' className='white-border'>
          <div>
            {' '}
            {moment(new Date(rule.createdOn)).format('DD MMM YYYY HH:mm:ss')}
          </div>
        </Col>
        <Col md='3' className='white-border'>
          {actionRow}
        </Col>
      </Row>
    );
  };

  const batchProcessRows =
    batchProcessList && batchProcessList.length > 0 ? (
      batchProcessList.map((batch) => renderBatchRow(batch))
    ) : !isLoading ? (
      <NoRowExistsRow emptyRowText='No rules exist' />
    ) : (
      <></>
    );

  const errorMessageRow =
    errorMessage !== '' ? <Alert color='danger'>{errorMessage}</Alert> : '';

  return (
    <div>
      <h4>Manage Batch Process</h4>
      {errorMessageRow}
      <Card>
        <CardBody>
          <Container fluid>
            <Row>
              <Form
                className='d-flex align-items-center flex-nowrap w-100 gap-0 px-0'
                onSubmit={(e) => e.preventDefault()}
              >
                <FormGroup className='d-flex align-items-center mb-0 me-2 flex-nowrap'>
                  <Label for='processCode' className='mb-0 me-2 text-nowrap'>
                    Process Name
                  </Label>
                  <Input
                    type='select'
                    name='processCode'
                    id='processCode'
                    value={form.processCode || ''}
                    onChange={handleInputChange}
                    className={errors.source ? 'error' : ''}
                    required
                  >
                    <option value='' disabled>
                      Process Name
                    </option>
                    <option value='SP'>Store Process</option>
                    <option value='FP'>Funder Process</option>
                    <option value='PA'>Purchase Rebate</option>
                  </Input>
                </FormGroup>
                <FormGroup className='d-flex align-items-center mb-0 me-2 flex-nowrap'>
                  <Label for='startDate' className='mb-0 me-2 text-nowrap'>
                    Start Date
                  </Label>
                  <DatePicker
                    id='startDate'
                    name='startDate'
                    required
                    selected={form.startDate}
                    dateFormat='DD-MMM-YYYY'
                    onChange={handleStartDateChange}
                    className={errors.startDate ? 'error' : 'form-control'}
                    locale='en-au'
                  />
                </FormGroup>
                <FormGroup className='d-flex align-items-center mb-0 me-2 flex-nowrap'>
                  <Label for='endDate' className='mb-0 me-2 text-nowrap'>
                    End Date
                  </Label>
                  <DatePicker
                    id='endDate'
                    name='endDate'
                    required
                    selected={form.endDate}
                    dateFormat='DD-MMM-YYYY'
                    onChange={handleEndDateChange}
                    className={errors.endDate ? 'error' : 'form-control'}
                  />
                </FormGroup>
                <FormGroup className='d-flex align-items-center mb-0 me-2 flex-nowrap'>
                  <Label for='source' className='mb-0 me-2 text-nowrap'>
                    Deal Funder
                  </Label>
                  <Input
                    type='select'
                    id='dealFunder'
                    name='dealFunder'
                    onChange={handleInputChange}
                    value={form.dealFunder}
                    disabled={!isFunderEnabled}
                  >
                    <option value=''>All</option>
                    {dealFunderList.map((funder) => (
                      <option key={funder.id} value={funder.id}>
                        {funder.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup className='d-flex align-items-center mb-0 me-2 flex-nowrap'>
                  <Button
                    color='primary'
                    name='btnProcess'
                    id='btnProcess'
                    onClick={() => formSubmit(false)}
                    disabled={!isFormValid}
                  >
                    Process
                  </Button>
                  <Button
                    className='ms-2'
                    color='primary'
                    name='btnProcess'
                    id='btnProcess'
                    onClick={() => formSubmit(true)}
                    disabled={!isFormValid}
                  >
                    Preview
                  </Button>
                </FormGroup>
              </Form>
            </Row>
            <Row>
              <Col></Col>
            </Row>
          </Container>
        </CardBody>
      </Card>
      <h5>Batch processes</h5>
      <Container fluid>
        <div>
          <Row>
            <Col md='1' className='column-header white-border'>
              Process Name
            </Col>
            <Col md='2' className='column-header white-border'>
              Date Range
            </Col>
            <Col md='1' className='column-header white-border'>
              Funder
            </Col>
            <Col md='1' className='column-header white-border'>
              Status
            </Col>
            <Col md='2' className='column-header white-border'>
              Files
            </Col>
            <Col md='2' className='column-header white-border'>
              Created On
            </Col>
            <Col md='3' className='column-header white-border'>
              Actions
            </Col>
          </Row>
          {batchProcessRows}
          {isLoading && (
            <Row>
              <Col>
                <BeatLoader color={'#004C5F'} loading={isLoading} />
              </Col>
            </Row>
          )}
        </div>
      </Container>
    </div>
  );
};

export default BatchProcessPage;
